// Client helpers for org_member_capabilities RPCs (SC-2).

import { requireSupabase } from "@/lib/supabase";
import { memberCapabilityRowsToOverrides } from "@/lib/capabilities/capabilityMerge";
import type { CapabilityOverrides } from "@/lib/capabilities/capabilityOverrides";

export async function fetchMyMemberCapabilityOverrides(
  organizationId: string,
): Promise<CapabilityOverrides> {
  try {
    const sb = requireSupabase();
    const { data, error } = await sb.rpc("list_my_member_capabilities", {
      p_org_id: organizationId,
    });
    if (error) {
      console.warn("[capabilities] list_my_member_capabilities failed", error.message);
      return {};
    }
    return memberCapabilityRowsToOverrides(
      (data ?? []) as Array<{ capability?: unknown; value?: unknown }>,
    );
  } catch (e) {
    console.warn("[capabilities] list_my_member_capabilities threw", e);
    return {};
  }
}

export async function fetchOrgMemberCapabilityMap(
  organizationId: string,
): Promise<Record<string, CapabilityOverrides>> {
  try {
    const sb = requireSupabase();
    const { data, error } = await sb.rpc("admin_list_member_capabilities", {
      p_org_id: organizationId,
    });
    if (error) {
      console.warn("[capabilities] admin_list_member_capabilities failed", error.message);
      return {};
    }
    const out: Record<string, CapabilityOverrides> = {};
    for (const row of (data ?? []) as Array<{
      user_id?: unknown;
      capability?: unknown;
      value?: unknown;
    }>) {
      const uid = typeof row.user_id === "string" ? row.user_id : "";
      if (!uid) continue;
      const parsed = memberCapabilityRowsToOverrides([row]);
      out[uid] = { ...(out[uid] ?? {}), ...parsed };
    }
    return out;
  } catch (e) {
    console.warn("[capabilities] admin_list_member_capabilities threw", e);
    return {};
  }
}

export async function setMemberAdsRunnerCapability(args: {
  organizationId: string;
  userId: string;
  value: boolean;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const sb = requireSupabase();
    const { error } = await sb.rpc("admin_set_member_capability", {
      p_org_id: args.organizationId,
      p_user_id: args.userId,
      p_capability: "is_ads_runner",
      p_value: args.value,
    });
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}
